import React, { useMemo } from 'react';
import * as XLSX from 'xlsx';

const SetoranReportTable = ({ bulan, periodeTitle, setoran, anggota, reportMeta = {} }) => {
  // Gunakan setoran yang sudah difilter dari Dashboard (tidak perlu filter ulang)
  const setoranBulanIni = useMemo(() => {
    return setoran;
  }, [setoran]);

  // Helper function to convert to number
  const toNumber = (val) => {
    if (val === null || val === undefined) return 0;
    const num = Number(val);
    return isNaN(num) ? 0 : num;
  };

  // Rounding functions
  const roundToHundreds = (val) => Math.round(val / 100) * 100;
  const floorToMultiple = (value, multiple) => Math.floor(value / multiple) * multiple; // Gunakan floor seperti iuranCalculator.js

  // Data per transaksi setoran (bisa multi-bulan untuk anggota yang sama)
  const tableRows = useMemo(() => {
    return setoranBulanIni
      .map((setor) => {
        const angg = anggota.find((a) => a.id === setor.anggota_id);
        const komp = setor.komponen || {};
        const pendapatanAsli = toNumber(setor.pendapatan_estimasi);
        const pendapatanBulat = roundToHundreds(pendapatanAsli);
        const jumlahIuran = toNumber(setor.iuran_bulanan);
        const idi = toNumber(setor.iuran_idi);
        const gasibu = toNumber(komp.iuranGasibu);

        return {
          id: setor.id,
          anggota_id: setor.anggota_id,
          nama: angg?.nama || `ID ${setor.anggota_id}`,
          jumlah_anak: angg?.jumlah_anak ?? '-',
          bulan: setor.bulan,
          pendapatan: pendapatanBulat,
          ibk: toNumber(komp.ibk),
          ibs: toNumber(komp.ibs),
          ibp: toNumber(komp.ibp),
          ibks: toNumber(komp.ibks),
          iip: toNumber(komp.iip),
          lainnya: toNumber(komp.lainnya),
          jumlahIuran: jumlahIuran,
          idi: idi,
          gasibu: gasibu,
          total: jumlahIuran + idi + gasibu,
          status: pendapatanBulat >= 7640144 ? 'Muzaki' : 'Belum Muzaki',
        };
      })
      .sort((a, b) => {
        const byName = a.nama.localeCompare(b.nama, 'id');
        if (byName !== 0) return byName;
        const byAnggota = Number(a.anggota_id) - Number(b.anggota_id);
        if (byAnggota !== 0) return byAnggota;
        return a.bulan.localeCompare(b.bulan);
      });
  }, [anggota, setoranBulanIni]);

  const displayRows = useMemo(() => {
    let currentNo = 0;
    return tableRows.map((row, index) => {
      const prev = tableRows[index - 1];
      const isFirstInAnggotaGroup = !prev || prev.anggota_id !== row.anggota_id;

      let rowSpan = 1;
      if (isFirstInAnggotaGroup) {
        currentNo += 1;
        for (let i = index + 1; i < tableRows.length; i += 1) {
          if (tableRows[i].anggota_id === row.anggota_id) {
            rowSpan += 1;
          } else {
            break;
          }
        }
      }

      return {
        ...row,
        displayNo: isFirstInAnggotaGroup ? currentNo : null,
        isFirstInAnggotaGroup,
        rowSpan,
      };
    });
  }, [tableRows]);

  // Total keseluruhan (row Jumlah)
  const totalRow = {
    pendapatan: tableRows.reduce((s, a) => s + a.pendapatan, 0),
    ibk: tableRows.reduce((s, a) => s + a.ibk, 0),
    ibs: tableRows.reduce((s, a) => s + a.ibs, 0),
    ibp: tableRows.reduce((s, a) => s + a.ibp, 0),
    ibks: tableRows.reduce((s, a) => s + a.ibks, 0),
    iip: tableRows.reduce((s, a) => s + a.iip, 0),
    lainnya: tableRows.reduce((s, a) => s + a.lainnya, 0),
    jumlahIuran: tableRows.reduce((s, a) => s + a.jumlahIuran, 0),
    idi: tableRows.reduce((s, a) => s + a.idi, 0),
    gasibu: tableRows.reduce((s, a) => s + a.gasibu, 0),
    total: tableRows.reduce((s, a) => s + a.total, 0),
  };

  // Persentase setoran (seperti di Excel: 100% IBK,100% IBS,75% IBP,100% IBKs,100% IIP,100% Lainnya)
  const persenSetoran = {
    ibk: 1,
    ibs: 1,
    ibp: 0.75,
    ibks: 1,
    iip: 1,
    lainnya: 1,
  };

  const jumlahDisetor = {
    ibk: totalRow.ibk * persenSetoran.ibk,
    ibs: totalRow.ibs * persenSetoran.ibs,
    ibp: totalRow.ibp * persenSetoran.ibp,
    ibks: totalRow.ibks * persenSetoran.ibks,
    iip: totalRow.iip * persenSetoran.iip,
    lainnya: totalRow.lainnya * persenSetoran.lainnya,
    // Gunakan floorToMultiple seperti di iuranCalculator.js, bukan roundToThousands
    // Total hanya dari komponen, TANPA IDI
    total: floorToMultiple(
      (totalRow.ibk * persenSetoran.ibk) +
      (totalRow.ibs * persenSetoran.ibs) +
      (totalRow.ibp * persenSetoran.ibp) +
      (totalRow.ibks * persenSetoran.ibks) +
      (totalRow.iip * persenSetoran.iip) +
      (totalRow.lainnya * persenSetoran.lainnya),
      1000
    ),
  };

  // Kas Kelas: total iuran anggota - jumlah disetor ke pusat
  // Pastikan kas kelas juga divisible by 1000 (seperti di iuranCalculator.js)
  let kasKelasBulat = totalRow.jumlahIuran - jumlahDisetor.total;
  if (kasKelasBulat % 1000 !== 0) {
    kasKelasBulat = floorToMultiple(kasKelasBulat, 1000);
  }
  const kasKelas = kasKelasBulat;

  // Rekapitulasi bawah
  const rekapitulasi = {
    iuranBulanan: jumlahDisetor.total,
    idi: totalRow.idi,
    kasKelas: kasKelas,
    lainnya: totalRow.gasibu,
    total: jumlahDisetor.total + totalRow.idi + totalRow.gasibu,
  };
  // Total sudah dibulatkan di jumlahDisetor, jadi totalBulat adalah langsung dari rekapitulasi.total
  const totalBulat = rekapitulasi.total;

  // Hitung jumlah orang yang menyetorkan setiap jenis iuran
  const jumlahPenyetorIuranBulanan = new Set(tableRows.filter(a => a.jumlahIuran > 0).map(a => a.nama)).size;
  const jumlahPenyetorIDI = new Set(tableRows.filter(a => a.idi > 0).map(a => a.nama)).size;
  const jumlahPenyetorLainnya = new Set(tableRows.filter(a => a.gasibu > 0).map(a => a.nama)).size;

  // Konstanta Nishob (dari iuranCalculator.js)
  const NISHOB = 7640144;

  // Fungsi export ke Excel
  const exportToExcel = () => {
    // Siapkan data untuk worksheet
    const excelData = [];
    
    // === HEADER/KOP ===
    // Judul
    excelData.push(['REKAPITULASI IURAN BULANAN KELAS']);
    excelData.push([]); // Baris kosong
    
    // Info kiri dan kanan
    const bulanTahun = bulan.split('-');
    const namaBulan = new Date(bulanTahun[0], bulanTahun[1] - 1).toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
    
    excelData.push([
      `Bulan/Tahun :`,
      namaBulan,
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      `Setor Tanggal :`,
      reportMeta.tanggalSetor || new Date().toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })
    ]);
    excelData.push([
      `Kelas :`,
      reportMeta.className || 'AI',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      `Penyetor :`,
      reportMeta.penyetor || 'Hasan'
    ]);
    excelData.push([
      `Kode Kelas :`,
      reportMeta.kodeKelas || '404',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      `Penerima :`,
      reportMeta.penerima || 'Yayasan Bangun Cinta Nusantara'
    ]);
    excelData.push([
      `Pembina :`,
      reportMeta.pembina || 'Achmad',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      `Jumlah Keseluruhan Anggota :`,
      `${anggota.length} orang`
    ]);
    excelData.push([
      `Bendahara :`,
      reportMeta.bendahara || 'Hasan',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      `Nishob :`,
      `Rp ${NISHOB.toLocaleString('id-ID')}`
    ]);
    
    excelData.push([]); // Baris kosong sebelum tabel
    
    // === TABEL DATA ===
    // Header tabel
    excelData.push(['No.', 'Nama Siswa', 'Jumlah Anak', 'Bulan', 'Pendapatan',
      'IBK (1.5%)', 'IBS (0.5%)', 'IBP (2%)', 'IBKs (0.5%)', 'IIP (2.5%)', 'Lainnya',
      'Jumlah Iuran', 'IDI', 'Gasibu/Sumatera/Dll', 'Total', 'Keterangan']);
    // Data anggota
    tableRows.forEach((a, idx) => {
      excelData.push([
        idx + 1,
        a.nama,
        a.jumlah_anak,
        a.bulan,
        a.pendapatan,
        a.ibk,
        a.ibs,
        a.ibp,
        a.ibks,
        a.iip,
        a.lainnya,
        a.jumlahIuran,
        a.idi,
        a.gasibu,
        a.total,
        a.status,
      ]);
    });
    // Baris Jumlah
    excelData.push(['Jumlah', '', '', '', totalRow.pendapatan,
      totalRow.ibk, totalRow.ibs, totalRow.ibp, totalRow.ibks, totalRow.iip,
      totalRow.lainnya, totalRow.jumlahIuran, totalRow.idi, totalRow.gasibu, totalRow.total, '']);
    // Persentase setoran
    excelData.push(['% Setoran', '', '', '', '', '100%', '100%', '75%', '100%', '100%', '100%', '', '', '', '', '']);
    // Jumlah disetor
    excelData.push(['Jumlah disetor', '', '', '', '',
      jumlahDisetor.ibk, jumlahDisetor.ibs, jumlahDisetor.ibp, jumlahDisetor.ibks,
      jumlahDisetor.iip, jumlahDisetor.lainnya, jumlahDisetor.total, totalRow.idi, totalRow.gasibu, '', '']);
    // Kas Kelas
    excelData.push(['IBP (25% menjadi Kas Kelas)', '', '', '', '', '', '', '', '', '', kasKelas, '', '', '', '', '']);
    // Rekapitulasi
    excelData.push(['Rekapitulasi', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '']);
    excelData.push(['a. Iuran Bulanan', '', '', '', '', '', '', '', '', '', rekapitulasi.iuranBulanan, `(${jumlahPenyetorIuranBulanan} orang)`, '', '', '', '']);
    excelData.push(['b. IDI', '', '', '', '', '', '', '', '', '', rekapitulasi.idi, `(${jumlahPenyetorIDI} orang)`, '', '', '', '']);
    excelData.push(['c. Gasibu/Sumatera/Lainnya', '', '', '', '', '', '', '', '', '', rekapitulasi.lainnya, `(${jumlahPenyetorLainnya} orang)`, '', '', '', '']);
    excelData.push(['Jumlah yang disetorkan', '', '', '', '', '', '', '', '', '', totalBulat, '', '', '', '', '']);

    const ws = XLSX.utils.aoa_to_sheet(excelData);
    
    // Merge cells untuk judul (A1:P1)
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 15 } }];
    
    // Set styling untuk judul (center align)
    const titleCell = ws['A1'];
    if (titleCell) {
      titleCell.alignment = { horizontal: 'center', vertical: 'center' };
    }
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, `Rekap_${bulan}`);
    XLSX.writeFile(wb, `Rekap_Iuran_${bulan}.xlsx`);
  };

  const formatRp = (val) => {
    if (val === undefined || val === null) val = 0;
    return `Rp ${Math.round(val).toLocaleString()}`;
  };

  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold">📋 Tabel Rekapitulasi Iuran - {periodeTitle || bulan}</h3>
        <button
          onClick={exportToExcel}
          className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded shadow"
        >
          📎 Unduh Excel
        </button>
      </div>
      <div className="overflow-x-auto shadow border rounded-lg">
        <table className="min-w-full bg-white text-sm">
          <thead className="bg-gray-200">
            <tr>
              <th rowSpan="2" className="border p-2">No.</th>
              <th rowSpan="2" className="border p-2">Nama Siswa</th>
              <th rowSpan="2" className="border p-2">Jml Anak</th>
              <th rowSpan="2" className="border p-2">Bulan</th>
              <th rowSpan="2" className="border p-2">Pendapatan</th>
              <th colSpan="7" className="border p-2 text-center">Komponen Iuran</th>
              <th rowSpan="2" className="border p-2">IDI</th>
              <th rowSpan="2" className="border p-2">Gasibu/Sumatera/Dll</th>
              <th rowSpan="2" className="border p-2">Total</th>
              <th rowSpan="2" className="border p-2">Keterangan</th>
            </tr>
            <tr className="bg-gray-100">
              <th className="border p-1">IBK (1.5%)</th><th className="border p-1">IBS (0.5%)</th>
              <th className="border p-1">IBP (2%)</th><th className="border p-1">IBKs (0.5%)</th>
              <th className="border p-1">IIP (2.5%)</th><th className="border p-1">Lainnya</th>
              <th className="border p-1">Jumlah Iuran</th>
            </tr>
          </thead>
          <tbody>
            {displayRows.map((angg, idx) => (
              <tr key={`${angg.id}-${idx}`} className="hover:bg-gray-50">
                {angg.isFirstInAnggotaGroup && (
                  <td rowSpan={angg.rowSpan} className="border p-2 text-center align-top">{angg.displayNo}</td>
                )}
                {angg.isFirstInAnggotaGroup && (
                  <td rowSpan={angg.rowSpan} className="border p-2 align-top">{angg.nama}</td>
                )}
                <td className="border p-2 text-center">{angg.jumlah_anak}</td>
                <td className="border p-2 text-center">{angg.bulan}</td>
                <td className="border p-2 text-right">{formatRp(angg.pendapatan)}</td>
                <td className="border p-2 text-right">{formatRp(angg.ibk)}</td>
                <td className="border p-2 text-right">{formatRp(angg.ibs)}</td>
                <td className="border p-2 text-right">{formatRp(angg.ibp)}</td>
                <td className="border p-2 text-right">{formatRp(angg.ibks)}</td>
                <td className="border p-2 text-right">{formatRp(angg.iip)}</td>
                <td className="border p-2 text-right">{formatRp(angg.lainnya)}</td>
                <td className="border p-2 text-right">{formatRp(angg.jumlahIuran)}</td>
                <td className="border p-2 text-right">{formatRp(angg.idi)}</td>
                <td className="border p-2 text-right">{formatRp(angg.gasibu)}</td>
                <td className="border p-2 text-right font-bold">{formatRp(angg.total)}</td>
                <td className="border p-2">{angg.status}</td>
              </tr>
            ))}
            {tableRows.length === 0 && (
              <tr>
                <td colSpan="16" className="border p-3 text-center text-gray-500">Tidak ada data setoran pada periode ini.</td>
              </tr>
            )}
            {/* Baris Jumlah */}
            <tr className="bg-gray-100 font-semibold">
              <td colSpan="4" className="border p-2 text-right">Jumlah</td>
              <td className="border p-2 text-right">{formatRp(totalRow.pendapatan)}</td>
              <td className="border p-2 text-right">{formatRp(totalRow.ibk)}</td>
              <td className="border p-2 text-right">{formatRp(totalRow.ibs)}</td>
              <td className="border p-2 text-right">{formatRp(totalRow.ibp)}</td>
              <td className="border p-2 text-right">{formatRp(totalRow.ibks)}</td>
              <td className="border p-2 text-right">{formatRp(totalRow.iip)}</td>
              <td className="border p-2 text-right">{formatRp(totalRow.lainnya)}</td>
              <td className="border p-2 text-right">{formatRp(totalRow.jumlahIuran)}</td>
              <td className="border p-2 text-right">{formatRp(totalRow.idi)}</td>
              <td className="border p-2 text-right">{formatRp(totalRow.gasibu)}</td>
              <td className="border p-2 text-right">{formatRp(totalRow.total)}</td>
              <td className="border p-2"></td>
            </tr>
            {/* % Setoran */}
            <tr>
              <td colSpan="5" className="border p-2 text-right">% Setoran</td>
              <td className="border p-2 text-center">100%</td><td className="border p-2 text-center">100%</td>
              <td className="border p-2 text-center">75%</td><td className="border p-2 text-center">100%</td>
              <td className="border p-2 text-center">100%</td><td className="border p-2 text-center">100%</td>
              <td colSpan="5" className="border p-2"></td>
            </tr>
            {/* Jumlah disetor */}
            <tr className="bg-blue-50">
              <td colSpan="5" className="border p-2 text-right font-semibold">Jumlah disetor</td>
              <td className="border p-2 text-right">{formatRp(jumlahDisetor.ibk)}</td>
              <td className="border p-2 text-right">{formatRp(jumlahDisetor.ibs)}</td>
              <td className="border p-2 text-right">{formatRp(jumlahDisetor.ibp)}</td>
              <td className="border p-2 text-right">{formatRp(jumlahDisetor.ibks)}</td>
              <td className="border p-2 text-right">{formatRp(jumlahDisetor.iip)}</td>
              <td className="border p-2 text-right">{formatRp(jumlahDisetor.lainnya)}</td>
              <td className="border p-2 text-right font-bold">{formatRp(jumlahDisetor.total)}</td>
              <td className="border p-2 text-right">{formatRp(totalRow.idi)}</td>
              <td className="border p-2 text-right">{formatRp(totalRow.gasibu)}</td>
              <td className="border p-2 text-right font-bold">{formatRp(jumlahDisetor.total + totalRow.idi + totalRow.gasibu)}</td>
              <td className="border p-2"></td>
            </tr>
            {/* Kas Kelas */}
            <tr>
              <td colSpan="12" className="border p-2 text-right">IBP (25% menjadi Kas Kelas)</td>
              <td colSpan="4" className="border p-2 text-right font-semibold">{formatRp(kasKelas)}</td>
            </tr>
            {/* Rekapitulasi */}
            <tr className="bg-gray-50">
              <td colSpan="12" className="border p-2 font-bold">Rekapitulasi</td>
              <td colSpan="4" className="border p-2"></td>
            </tr>
            <tr>
              <td colSpan="12" className="border p-2">a. Iuran Bulanan</td>
              <td colSpan="4" className="border p-2 text-right">{formatRp(rekapitulasi.iuranBulanan)}</td>
            </tr>
            <tr>
              <td colSpan="12" className="border p-2">b. IDI</td>
              <td colSpan="4" className="border p-2 text-right">{formatRp(rekapitulasi.idi)}</td>
            </tr>
            <tr>
              <td colSpan="12" className="border p-2">c. Gasibu/Sumatera/Lainnya</td>
              <td colSpan="4" className="border p-2 text-right">{formatRp(rekapitulasi.lainnya)}</td>
            </tr>
            <tr className="bg-yellow-100 font-bold">
              <td colSpan="12" className="border p-2">Jumlah yang disetorkan</td>
              <td colSpan="4" className="border p-2 text-right">{formatRp(totalBulat)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SetoranReportTable;
